import { useState, useEffect, useMemo, useRef } from 'react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import type { ChatConfig } from '@dev-streetbeat/chat';

import { auth } from '../config/firebase';
import JwtAuthService from '../services/JwtAuthService';
import FirebaseTokenService from '../services/FirebaseTokenService';

interface UseChatConfigResult {
  chatConfig: ChatConfig;
  loading: boolean;
  error: string | null;
}

export const useChatConfig = (): UseChatConfigResult => {
  const [firebaseKey, setFirebaseKey] = useState<string | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const retryCount = useRef<number>(0);

  // Get authentication token
  useEffect(() => {
    const getAuthToken = async () => {
      try {
        if (JwtAuthService.shouldUseJwtAuth()) {
          // For JWT auth, get JWT token
          const token = JwtAuthService.getToken();
          setJwtToken(token);
          setFirebaseKey(null);
          setLoading(false);
        } else {
          await auth.authStateReady();
          const token = await auth.currentUser?.getIdToken();
          setFirebaseKey(token || null);
          setJwtToken(null);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to get auth token:', err);
        setError('Authentication failed');
        setLoading(false);
      }
    };

    getAuthToken();
  }, []);

  // Create base URL from runtime config
  const baseUrl = window.__APP_CONFIG__?.API_BASE_URL;
  const tenantId = window.__APP_CONFIG__?.TENANT_ID;
  const assistantName = window.__APP_CONFIG__?.ASSISTANT_NAME;

  // Create chat config with appropriate headers
  const chatConfig: ChatConfig = useMemo(() => {
    const headers: Record<string, string | undefined> = {
      AppVersionEpoch: '99999999999',
      'X-Tenant-Id': tenantId,
    };

    // Add appropriate auth header
    if (jwtToken) {
      headers['Jwt-Jwks'] = jwtToken;
    } else if (firebaseKey) {
      headers['Firebase'] = firebaseKey;
    }

    return {
      baseUrl,
      requestHeadersMap: headers,
      axiosInterceptors: {
        request: {
          onFulfilled: async (config) => {
            // Check and refresh token before each request
            if (!JwtAuthService.shouldUseJwtAuth()) {
              const validToken = await FirebaseTokenService.getValidToken(firebaseKey);
              if (validToken) {
                config.headers.Firebase = validToken;

                // Update state if token was refreshed
                if (validToken !== firebaseKey) {
                  setFirebaseKey(validToken);
                }
              }
            }
            return config;
          },
        },
        response: {
          onRejected: async (error: AxiosError & { config: AxiosRequestConfig }) => {
            if (error.response?.status === 401 && error.config) {
              if (retryCount.current >= 3) {
                console.error('Max retry attempts (3) reached for 401 error. Authentication failed.');
                return Promise.reject(error);
              }
              retryCount.current++;
              try {
                // refresh firebase token
                const token = await FirebaseTokenService.refreshToken();
                if (token) {
                  // Update the stored token
                  setFirebaseKey(token);

                  // Update the failed request headers and retry
                  error.config.headers.Firebase = token;

                  // Retry the request with the new token
                  const response = await axios.request(error.config);

                  retryCount.current = 0;

                  return response;
                }
              } catch (refreshError) {
                console.error(`Failed to refresh Firebase token on attempt ${retryCount.current}:`, refreshError);

                // If this was the last retry attempt, reject with the refresh error
                if (retryCount.current >= 3) {
                  console.error('Max retry attempts reached. Authentication failed.');
                  return Promise.reject(refreshError);
                }

                // Otherwise, continue to retry
                return Promise.reject(error);
              }
            }
            return Promise.reject(error);
          },
        },
      },
      debugMode: process.env.NODE_ENV === 'development',
      assistantName,
    };
  }, [baseUrl, firebaseKey, jwtToken, tenantId, assistantName]);

  return { chatConfig, loading, error };
};
